import AddIcon from "@mui/icons-material/Add";
import CancelScheduleSendOutlinedIcon from "@mui/icons-material/CancelScheduleSendOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import UploadFile from "components/Input/UploadFile";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { formatDateForInput } from "views/admin/Accounts/accountUtils";
import PaymentReceiptChips from "components/Payments/PaymentReceiptChips";
import {
  createEmptyPaymentDraft,
  canDeleteVerifiedPaymentFromTermPayments,
  formatAudCompact,
  isRefundEntry,
  pendingRefundTotalFromTermPayments,
  refundableAvailableFromTermPayments,
  sumPaymentsByStatus,
} from "./termPaymentUtils";
import {
  addTermPayment,
  deleteTermPaymentAsSuperAdmin,
  resubmitTermPayment,
  syncTermPayments,
} from "../admissionApi";
import { isSuperAdmin, canVerifyPayments, canRequestCreditAdjustment } from "utils/permissionUtils";
import CreditAdjustmentDialog from "./CreditAdjustmentDialog";
import PendingPaymentDialog from "components/Payments/PendingPaymentDialog";
import { canChangePendingPayment } from "utils/pendingPaymentChange";
import {
  editPendingTermPayment,
  withdrawTermPayment,
} from "views/admin/Accounts/verification/verificationTaskApi";
import {
  creditAdjustmentLabel,
  isCreditAdjustment,
  termCreditAvailable,
} from "./creditAdjustmentUtils";

const buildPaymentPayload = (payments) =>
  payments.map((p) => ({
    uuid: p.id,
    amount: p.amount,
    entryType: isRefundEntry(p.entryType) ? "REFUND" : "PAYMENT",
    date: p.date,
    reference: p.reference,
    notes: p.notes,
    attachments: p.attachments,
    notifyAccounts: p.notifyAccounts,
    createdAt: p.createdAt,
  }));

const paymentToDraft = (payment) => ({
  amount: payment.amount ?? "",
  entryType: isRefundEntry(payment.entryType) ? "REFUND" : "PAYMENT",
  date: formatDateForInput(payment.date) || payment.date || "",
  reference: payment.reference ?? "",
  notes: payment.notes ?? "",
});

const PaymentListItem = ({
  payment,
  readOnly,
  onRemove,
  onEditRejected,
  onChangePending,
  superAdmin = false,
  onSuperAdminDelete,
  superAdminDeleteBlockReason = null,
}) => {
  const hasNotes = Boolean(payment.notes?.trim());
  const attachments = payment.attachments || [];
  const adjustment = isCreditAdjustment(payment);
  const refund = isRefundEntry(payment.entryType);
  // A credit move is one half of a pair the schedule never owns, so it cannot be dropped from here.
  const isLocked =
    adjustment ||
    payment.verificationStatus === "PENDING_VERIFICATION" ||
    payment.verificationStatus === "VERIFIED";
  const isRejected = payment.verificationStatus === "REJECTED";
  // Only whoever sent it (or a super admin) may still correct or take back a pending request.
  const canChangePending =
    Boolean(onChangePending && payment.backendId) &&
    canChangePendingPayment({ ...payment, status: payment.verificationStatus });
  // A schedule-only entry lives in the offer-letter schedule JSON with no backend payment record
  // (status NONE, no backendId) — e.g. legacy-migrated, imported, or a deleted payment lingering in
  // the JSON. It carries no verified amount, so it can be removed even on a locked term.
  const isScheduleOnly =
    !payment.backendId && (!payment.verificationStatus || payment.verificationStatus === "NONE");

  return (
    <Paper variant="outlined" sx={{ p: 2, ...(refund && { borderColor: "error.light" }) }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color={refund ? "error.main" : "text.primary"}
            >
              {refund ? "− " : ""}
              {formatAudCompact(Number(payment.amount))}
            </Typography>
            {adjustment ? (
              <Chip
                icon={<SwapHorizOutlinedIcon />}
                label={creditAdjustmentLabel(payment)}
                size="small"
                color="info"
                variant="outlined"
              />
            ) : refund ? (
              <Chip label="Refund" size="small" color="error" variant="outlined" />
            ) : null}
            <Typography variant="body2" color="text.secondary">
              · {formatDateForInput(payment.date) || "—"}
            </Typography>
            {payment.reference ? (
              <Chip label={payment.reference} size="small" variant="outlined" />
            ) : null}
            {payment.notifyAccounts ? (
              <Chip
                icon={<NotificationsActiveOutlinedIcon />}
                label="Accounts"
                size="small"
                color="info"
                variant="outlined"
              />
            ) : null}
            {payment.verificationStatus && payment.verificationStatus !== "NONE" && (
              <Chip
                label={
                  payment.verificationStatus === "VERIFIED"
                    ? "Verified"
                    : payment.verificationStatus === "REJECTED"
                    ? "Rejected — edit & resubmit"
                    : "Pending verification"
                }
                size="small"
                color={
                  payment.verificationStatus === "VERIFIED"
                    ? "success"
                    : payment.verificationStatus === "REJECTED"
                    ? "error"
                    : "warning"
                }
                variant="outlined"
              />
            )}
          </Box>

          {isRejected && payment.rejectionFeedback && (
            <Typography variant="caption" color="error.main" sx={{ display: "block", mb: 0.5 }}>
              Rejection reason: {payment.rejectionFeedback}
            </Typography>
          )}

          {payment.verificationStatus === "PENDING_VERIFICATION" && payment.lastEditedByName && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              Edited by {payment.lastEditedByName} while pending
            </Typography>
          )}

          {hasNotes && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: "pre-wrap", mb: 1 }}
            >
              {payment.notes}
            </Typography>
          )}

          {attachments.length > 0 && <PaymentReceiptChips urls={attachments} sx={{}} />}
        </Box>

        <div className="flex flex-col items-end justify-start gap-1">
          {canChangePending && (
            <div className="flex justify-end gap-2">
              <Tooltip
                title={
                  adjustment
                    ? "Change the reason before it is verified"
                    : "Edit before it is verified"
                }
              >
                <IconButton size="small" onClick={() => onChangePending("edit", payment)}>
                  <EditNoteOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Withdraw from verification">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onChangePending("withdraw", payment)}
                >
                  <CancelScheduleSendOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          )}
          {!readOnly && (
            <div className="flex justify-end gap-2">
              {isRejected && onEditRejected && (
                <Tooltip
                  title={
                    adjustment
                      ? "Update the reason and resubmit the move"
                      : "Edit details and resubmit"
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      color="warning"
                      onClick={() => onEditRejected(payment)}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip
                title={
                  adjustment
                    ? "A credit move is undone by rejecting it, or by a super admin delete"
                    : isLocked
                    ? "Cannot remove a payment that is pending or verified"
                    : "Remove payment"
                }
              >
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={isLocked}
                    onClick={() => !isLocked && onRemove(payment.id)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          )}
          {superAdmin && payment.backendId && (
            <Tooltip
              title={superAdminDeleteBlockReason || "Super admin: permanently delete this payment"}
            >
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={Boolean(superAdminDeleteBlockReason)}
                  onClick={() => !superAdminDeleteBlockReason && onSuperAdminDelete(payment)}
                >
                  <DeleteForeverOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {readOnly && isScheduleOnly && (
            <Tooltip title="Remove this unsynced schedule line — not a verified payment, does not affect the paid total">
              <span>
                <IconButton size="small" color="error" onClick={() => onRemove(payment.id)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </div>
      </Box>
    </Paper>
  );
};

export default function TermPaymentsModal({
  open,
  onClose,
  termLabel,
  clientName = "",
  agreedAmount = 0,
  payments = [],
  readOnly = false,
  onChangePayments,
  backendPayments = [],
  termId = null,
  admissionId = null,
  termIndex = null,
  onPaymentsSynced,
  coeReceived = true,
  coeFeePaymentUuid = null,
  ensureTermReady = null,
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(createEmptyPaymentDraft);
  const [uploadData, setUploadData] = useState({ attachments: [] });
  const [isSyncing, setIsSyncing] = useState(false);
  const [resolvedTermId, setResolvedTermId] = useState(null);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [autoVerify, setAutoVerify] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, payment: null });
  const [removeDialog, setRemoveDialog] = useState({ open: false, paymentId: null });
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [resubmitMove, setResubmitMove] = useState({ payment: null, reason: "" });
  const [pendingChange, setPendingChange] = useState({ mode: null, payment: null });

  const superAdmin = isSuperAdmin();
  // Account and super-admin users can both submit and verify, so they may verify their own entries in
  // one step. Default the toggle ON for them; the super-admin-only payment delete still uses `superAdmin`.
  const canSelfVerify = canVerifyPayments();
  const willAutoVerify = canSelfVerify && autoVerify;

  const paymentsWithStatus = useMemo(() => {
    return payments.map((p) => {
      const backend = backendPayments.find((b) => b.uuid === p.id);
      return {
        ...p,
        backendId: backend?.id ?? null,
        adjustmentUuid: backend?.adjustmentUuid ?? null,
        adjustmentTermNumber: backend?.adjustmentTermNumber ?? null,
        entryType: backend?.entryType ?? p.entryType ?? "PAYMENT",
        verificationStatus: backend?.status ?? p._localStatus ?? "NONE",
        rejectionFeedback: backend?.rejectionFeedback ?? null,
        coeFee: backend?.coeFee ?? false,
        revision: backend?.revision ?? null,
        submittedById: backend?.submittedById ?? null,
        lastEditedByName: backend?.lastEditedByName ?? null,
      };
    });
  }, [payments, backendPayments]);

  const handleSuperAdminDelete = (payment) => {
    if (!payment?.backendId) return;
    const block = canDeleteVerifiedPaymentFromTermPayments(backendPayments, payment.backendId);
    if (!block.allowed) {
      toast.error(block.reason);
      return;
    }
    setDeleteDialog({ open: true, payment });
  };

  const deleteDialogBlockReason = useMemo(() => {
    const payment = deleteDialog.payment;
    if (!payment?.backendId) return null;
    const block = canDeleteVerifiedPaymentFromTermPayments(backendPayments, payment.backendId);
    return block.allowed ? null : block.reason;
  }, [deleteDialog.payment, backendPayments]);

  const resolveSuperAdminDeleteBlock = (payment) => {
    if (!payment?.backendId) return null;
    const block = canDeleteVerifiedPaymentFromTermPayments(backendPayments, payment.backendId);
    return block.allowed ? null : block.reason;
  };

  const confirmSuperAdminDelete = async () => {
    const payment = deleteDialog.payment;
    if (!payment?.backendId) {
      setDeleteDialog({ open: false, payment: null });
      return;
    }
    const block = canDeleteVerifiedPaymentFromTermPayments(backendPayments, payment.backendId);
    if (!block.allowed) {
      toast.error(block.reason);
      setDeleteDialog({ open: false, payment: null });
      return;
    }
    setIsSyncing(true);
    try {
      await deleteTermPaymentAsSuperAdmin(payment.backendId);
      onChangePayments?.(payments.filter((p) => p.id !== payment.id));
      await queryClient.invalidateQueries({ queryKey: ["admissionTerms"] });
      onPaymentsSynced?.();
      if (isCreditAdjustment(payment)) {
        await queryClient.invalidateQueries({ queryKey: ["credit-adjustment-options"] });
      }
      toast.success(
        isCreditAdjustment(payment)
          ? "Credit move deleted from both terms."
          : `${isRefundEntry(payment.entryType) ? "Refund" : "Payment"} deleted.`
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.errorMessage ||
          err?.response?.data?.message ||
          "Failed to delete payment."
      );
    } finally {
      setIsSyncing(false);
      setDeleteDialog({ open: false, payment: null });
    }
  };

  const hasUnsaved = payments.some((p) => !backendPayments.find((b) => b.uuid === p.id));
  const hasDeletions = backendPayments.some(
    (b) =>
      !isCreditAdjustment(b) &&
      b.status !== "VERIFIED" &&
      b.status !== "PENDING_VERIFICATION" &&
      !payments.find((p) => p.id === b.uuid)
  );
  const hasPendingChanges = hasUnsaved || hasDeletions;

  const syncPaymentsToBackend = async () => {
    if (!termId) return;
    await syncTermPayments(termId, buildPaymentPayload(payments));
    await queryClient.invalidateQueries({ queryKey: ["admissionTerms"] });
    onPaymentsSynced?.();
  };

  const handleClose = async () => {
    if (isSyncing) return;
    if (hasPendingChanges) {
      setIsSyncing(true);
      try {
        await syncPaymentsToBackend();
      } catch {
        toast.error("Failed to save payments.");
      } finally {
        setIsSyncing(false);
      }
    }
    setEditingPaymentId(null);
    setConfirmOpen(false);
    onClose();
  };

  const { verified: verifiedPaid } = sumPaymentsByStatus(payments, backendPayments);
  const verifiedPaidClamped = Math.max(0, verifiedPaid);
  const pendingRefundTotal = pendingRefundTotalFromTermPayments(backendPayments);
  const refundableAvailable = refundableAvailableFromTermPayments(backendPayments);
  const due = Math.max(0, agreedAmount - verifiedPaidClamped);
  const creditAvailable = termCreditAvailable(backendPayments, agreedAmount);
  const canMoveCredit = Boolean(termId) && creditAvailable > 0 && canRequestCreditAdjustment();

  const superAdminRecovery =
    superAdmin && readOnly && verifiedPaidClamped === 0 && due > 0 && !editingPaymentId;

  const lockedVerifiedTerm = readOnly && !superAdminRecovery;
  const refundEntryDisabled = refundableAvailable <= 0;

  const defaultDraftEntryType = () => (due <= 0 ? "REFUND" : "PAYMENT");

  useEffect(() => {
    if (open) {
      // Default entry type from remaining due: payment when due > 0, refund when fully paid.
      setDraft({ ...createEmptyPaymentDraft(), entryType: defaultDraftEntryType() });
      setUploadData({ attachments: [] });
      setEditingPaymentId(null);
      setConfirmOpen(false);
      setAutoVerify(true);
      setResolvedTermId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, due, readOnly, superAdminRecovery]);

  useEffect(() => {
    if (termId) setResolvedTermId(null);
  }, [termId]);

  const resetDraft = () => {
    setDraft({ ...createEmptyPaymentDraft(), entryType: defaultDraftEntryType() });
    setUploadData({ attachments: [] });
    setEditingPaymentId(null);
  };

  const resolveActiveTermId = async () => {
    if (termId) return termId;
    if (resolvedTermId) return resolvedTermId;
    if (!ensureTermReady) return null;
    setIsSyncing(true);
    try {
      const id = await ensureTermReady(termIndex);
      if (id) setResolvedTermId(id);
      return id ?? null;
    } finally {
      setIsSyncing(false);
    }
  };

  const validateDraft = (activeTermId) => {
    const amt = Number(draft.amount);
    const refund = isRefundEntry(draft.entryType);
    const noun = refund ? "refund" : "payment";
    if (!amt || amt <= 0) {
      toast.warning(`Enter a valid ${noun} amount.`);
      return false;
    }
    if (!draft.date) {
      toast.warning(`Enter a ${noun} date.`);
      return false;
    }
    if (!activeTermId) {
      toast.info("Save the admission first to record payments.");
      return false;
    }
    if (refund) {
      const available = refundableAvailableFromTermPayments(backendPayments);
      const pendingRefunds = pendingRefundTotalFromTermPayments(backendPayments);
      const verifiedClamped = Math.max(0, sumPaymentsByStatus(payments, backendPayments).verified);
      if (amt > available) {
        toast.warning(
          pendingRefunds > 0
            ? `Refund (${formatAudCompact(amt)}) exceeds the refundable amount (${formatAudCompact(
                available
              )}). Verified paid: ${formatAudCompact(
                verifiedClamped
              )}, pending refunds: ${formatAudCompact(pendingRefunds)}.`
            : `Refund (${formatAudCompact(
                amt
              )}) cannot exceed the verified paid amount (${formatAudCompact(verifiedClamped)}).`
        );
        return false;
      }
    }
    return true;
  };

  const submitAdd = async (activeTermId) => {
    setIsSyncing(true);
    const refund = isRefundEntry(draft.entryType);
    try {
      const payload = {
        uuid: crypto.randomUUID(),
        amount: String(Number(draft.amount)),
        entryType: refund ? "REFUND" : "PAYMENT",
        date: draft.date,
        reference: draft.reference.trim(),
        notes: draft.notes.trim(),
        attachments: [...(uploadData.attachments || [])],
        createdAt: new Date().toISOString(),
        autoVerify: willAutoVerify,
      };
      await addTermPayment(activeTermId, payload);
      onChangePayments?.([
        ...payments,
        {
          id: payload.uuid,
          amount: payload.amount,
          entryType: payload.entryType,
          date: payload.date,
          reference: payload.reference,
          notes: payload.notes,
          attachments: payload.attachments,
          createdAt: payload.createdAt,
          _localStatus: willAutoVerify ? "VERIFIED" : "PENDING_VERIFICATION",
        },
      ]);
      await queryClient.invalidateQueries({ queryKey: ["admissionTerms"] });
      onPaymentsSynced?.();
      resetDraft();
      setConfirmOpen(false);
      toast.success(
        willAutoVerify
          ? `${refund ? "Refund" : "Payment"} added and verified.`
          : `${refund ? "Refund" : "Payment"} added and sent for verification.`
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.errorMessage ||
          err?.response?.data?.message ||
          `Failed to add ${refund ? "refund" : "payment"}.`
      );
    } finally {
      setIsSyncing(false);
    }
  };

  const submitResubmit = async (activeTermId) => {
    if (!editingPaymentId) return;
    setIsSyncing(true);
    try {
      const body = {
        amount: String(Number(draft.amount)),
        date: draft.date,
        reference: draft.reference.trim(),
        notes: draft.notes.trim(),
        attachments: [...(uploadData.attachments || [])],
      };
      await resubmitTermPayment(activeTermId, editingPaymentId, body);
      onChangePayments?.(
        payments.map((p) =>
          p.id === editingPaymentId
            ? {
                ...p,
                amount: body.amount,
                date: body.date,
                reference: body.reference,
                notes: body.notes,
                attachments: body.attachments,
                _localStatus: "PENDING_VERIFICATION",
              }
            : p
        )
      );
      await queryClient.invalidateQueries({ queryKey: ["admissionTerms"] });
      onPaymentsSynced?.();
      resetDraft();
      setConfirmOpen(false);
      toast.success("Payment resubmitted for verification.");
    } catch (err) {
      toast.error(
        err?.response?.data?.errorMessage || err?.response?.data?.message || "Failed to resubmit."
      );
    } finally {
      setIsSyncing(false);
    }
  };

  const handleAddClick = async () => {
    if (coeBlocksSubmit) {
      toast.warning("COE must be received before sending a payment for verification.");
      return;
    }
    const activeTermId = await resolveActiveTermId();
    if (!validateDraft(activeTermId)) return;
    setConfirmOpen(true);
  };

  const handleResubmitClick = async () => {
    if (coeBlocksSubmit) {
      toast.warning("COE must be received before sending a payment for verification.");
      return;
    }
    const activeTermId = await resolveActiveTermId();
    if (!validateDraft(activeTermId)) return;
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    const activeTermId = (await resolveActiveTermId()) ?? termId ?? resolvedTermId;
    if (!activeTermId) {
      toast.info("Save the admission first to record payments.");
      return;
    }
    if (editingPaymentId) submitResubmit(activeTermId);
    else submitAdd(activeTermId);
  };

  const handleEditRejected = (payment) => {
    if (isCreditAdjustment(payment)) {
      setResubmitMove({ payment, reason: payment.notes ?? "" });
      return;
    }
    setEditingPaymentId(payment.id);
    setDraft(paymentToDraft(payment));
    setUploadData({ attachments: [...(payment.attachments || [])] });
  };

  const confirmResubmitMove = async () => {
    const { payment, reason } = resubmitMove;
    if (!payment || !termId) return;
    if (!reason.trim()) {
      toast.warning("A reason is required for a credit adjustment.");
      return;
    }
    setIsSyncing(true);
    try {
      await resubmitTermPayment(termId, payment.id, { notes: reason.trim() });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admissionTerms"] }),
        queryClient.invalidateQueries({ queryKey: ["pending-payment-verifications"] }),
      ]);
      onPaymentsSynced?.();
      setResubmitMove({ payment: null, reason: "" });
      toast.success("Credit move resubmitted for verification.");
    } catch (err) {
      toast.error(
        err?.response?.data?.errorMessage ||
          err?.response?.data?.message ||
          "Failed to resubmit the credit move."
      );
    } finally {
      setIsSyncing(false);
    }
  };

  /**
   * The dialog works on server ids and revisions, the schedule list on uuids: hand it the server id
   * as id and keep the uuid alongside so the local list can follow the change.
   */
  const openPendingChange = (mode, payment) =>
    setPendingChange({
      mode,
      payment: {
        ...payment,
        id: payment.backendId,
        uuid: payment.id,
        date: formatDateForInput(payment.date) || payment.date || "",
      },
    });

  const refreshAfterPendingChange = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["admissionTerms"] }),
      queryClient.invalidateQueries({ queryKey: ["pending-payment-verifications"] }),
    ]);
    onPaymentsSynced?.();
  };

  const applyLocalValues = (uuid, values) =>
    onChangePayments?.(
      payments.map((p) =>
        p.id === uuid
          ? {
              ...p,
              amount: values.amount ?? p.amount,
              date: values.date ?? p.date,
              reference: values.reference ?? "",
              notes: values.notes ?? "",
              attachments: values.attachments ?? p.attachments,
            }
          : p
      )
    );

  const termPaymentBody = (payment, values) =>
    isCreditAdjustment(payment)
      ? { amount: payment.amount, date: payment.date, notes: values.notes }
      : {
          amount: String(Number(values.amount)),
          date: values.date,
          reference: values.reference,
          notes: values.notes,
          attachments: values.attachments,
        };

  const handleRemove = (paymentId) => {
    const payment = paymentsWithStatus.find((p) => p.id === paymentId);
    if (
      payment?.verificationStatus === "PENDING_VERIFICATION" ||
      payment?.verificationStatus === "VERIFIED"
    ) {
      toast.info("Cannot remove a payment that is pending or verified.");
      return;
    }
    setRemoveDialog({ open: true, paymentId });
  };

  const confirmRemove = () => {
    const paymentId = removeDialog.paymentId;
    setRemoveDialog({ open: false, paymentId: null });
    if (paymentId == null) return;
    onChangePayments(payments.filter((p) => p.id !== paymentId));
  };

  const canRefund = refundableAvailable > 0;
  const showEntryForm = !readOnly || canRefund || superAdminRecovery || lockedVerifiedTerm;
  const draftIsRefund = isRefundEntry(draft.entryType);
  const refundAllowedWithoutCoe = draftIsRefund && verifiedPaidClamped > 0;
  // The COE fee payment is the one payment recorded BEFORE the COE exists, so resubmitting it after a
  // rejection cannot require the COE — that would strand the admission with no way to correct it. The
  // backend already exempts this uuid (TermPaymentCoeGuard#isCoeFeeLinkedPayment); this mirrors it so
  // the button is not dead.
  const editingPayment = editingPaymentId
    ? paymentsWithStatus.find((p) => p.id === editingPaymentId)
    : null;
  const resubmittingCoeFeePayment = Boolean(
    editingPaymentId &&
      (editingPayment?.coeFee ||
        // Admissions that predate the coe_fee column carry the link as a single uuid instead.
        (coeFeePaymentUuid && editingPaymentId === coeFeePaymentUuid))
  );
  const coeBlocksSubmit = !coeReceived && !refundAllowedWithoutCoe && !resubmittingCoeFeePayment;
  const formTitle = editingPaymentId
    ? `Edit rejected ${draftIsRefund ? "refund" : "payment"}`
    : `Record new ${draftIsRefund ? "refund" : "payment"}`;
  const primaryLabel = editingPaymentId
    ? "Resubmit for verification"
    : draftIsRefund
    ? "Add refund"
    : "Add payment";

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
            <span>Payments — {termLabel}</span>
            {clientName ? (
              <Chip label={clientName} size="small" variant="outlined" sx={{ fontWeight: 500 }} />
            ) : null}
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ bgcolor: "background.default" }}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "background.paper" }}>
            <Stack direction="row" flexWrap="wrap" gap={2} useFlexGap>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Agreed
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {formatAudCompact(agreedAmount)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Paid (verified)
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={600}
                  color={verifiedPaidClamped > 0 ? "success.main" : "text.primary"}
                >
                  {formatAudCompact(verifiedPaidClamped)}
                </Typography>
                {pendingRefundTotal > 0 && (
                  <Typography variant="caption" color="warning.main" display="block">
                    {formatAudCompact(pendingRefundTotal)} refund pending verification
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Due
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={600}
                  color={due > 0 ? "warning.main" : "success.main"}
                >
                  {formatAudCompact(due > 0 ? due : 0)}
                </Typography>
                {creditAvailable > 0 && (
                  <Typography variant="caption" color="success.main" display="block">
                    {formatAudCompact(creditAvailable)} credit
                  </Typography>
                )}
              </Box>
              {canMoveCredit && (
                <Box sx={{ alignSelf: "center" }}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    startIcon={<SwapHorizOutlinedIcon />}
                    onClick={() => setCreditDialogOpen(true)}
                    disabled={isSyncing}
                  >
                    Move credit
                  </Button>
                </Box>
              )}
              <Box sx={{ ml: "auto" }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Payments recorded
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {payments.length}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Accordion
            defaultExpanded={false}
            disableGutters
            sx={{
              mb: 3,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "background.paper",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48 }}>
              <Typography variant="subtitle2">
                Payment history
                {paymentsWithStatus.length > 0 ? (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 1, fontWeight: 400 }}
                  >
                    ({paymentsWithStatus.length}{" "}
                    {paymentsWithStatus.length === 1 ? "entry" : "entries"})
                  </Typography>
                ) : null}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 2 }}>
              {paymentsWithStatus.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No payments recorded for this term yet.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {[...paymentsWithStatus]
                    .sort((a, b) => {
                      const da = a.date ? new Date(a.date).getTime() : 0;
                      const db = b.date ? new Date(b.date).getTime() : 0;
                      return db - da;
                    })
                    .map((p) => (
                      <PaymentListItem
                        key={p.id}
                        payment={p}
                        readOnly={readOnly}
                        onRemove={handleRemove}
                        onEditRejected={termId ? handleEditRejected : undefined}
                        onChangePending={openPendingChange}
                        superAdmin={superAdmin}
                        onSuperAdminDelete={handleSuperAdminDelete}
                        superAdminDeleteBlockReason={resolveSuperAdminDeleteBlock(p)}
                      />
                    ))}
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>

          {readOnly && !showEntryForm && superAdmin && (
            <Alert severity="info" sx={{ mt: 1 }}>
              This term is locked after import. Close this dialog, click the <strong>unlock</strong>{" "}
              icon next to the term in the schedule, then reopen payments to add entries.
            </Alert>
          )}

          {showEntryForm && (
            <>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                {formTitle}
              </Typography>

              {coeBlocksSubmit && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  COE not received yet. Mark <strong>COE received</strong> on the admission
                  application before sending term payments for verification.
                </Alert>
              )}

              {superAdminRecovery && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This term is verified/imported with no verified payments left. As super admin you
                  can record a new payment here to restore the term ledger.
                </Alert>
              )}

              {lockedVerifiedTerm && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {due > 0 ? (
                    <>
                      This term is verified/imported. Record an additional <strong>payment</strong>{" "}
                      toward the remaining due, or a <strong>refund</strong> to reduce verified paid
                      (refunds are sent for verification and reopen the term when verified).
                    </>
                  ) : (
                    <>
                      This term is fully paid and verified/imported. Record an additional{" "}
                      <strong>payment</strong> or a <strong>refund</strong> to reduce verified paid
                      (refunds are sent for verification and reopen the term when verified).
                    </>
                  )}
                </Alert>
              )}

              <Paper variant="outlined" sx={{ p: 2, bgcolor: "background.paper" }}>
                <Stack spacing={2}>
                  <Alert severity="info" icon={<InfoOutlinedIcon fontSize="inherit" />}>
                    Your added receipts and notes will be added into term notes after verification.
                  </Alert>
                  {!editingPaymentId && (
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      color={draftIsRefund ? "error" : "primary"}
                      value={draftIsRefund ? "REFUND" : "PAYMENT"}
                      onChange={(_e, val) => {
                        if (val) setDraft((d) => ({ ...d, entryType: val }));
                      }}
                    >
                      <ToggleButton value="PAYMENT">Payment</ToggleButton>
                      <ToggleButton value="REFUND" disabled={refundEntryDisabled}>
                        Refund
                      </ToggleButton>
                    </ToggleButtonGroup>
                  )}
                  {canSelfVerify && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={autoVerify}
                          onChange={(e) => setAutoVerify(e.target.checked)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" color="text.secondary">
                          Verify immediately
                        </Typography>
                      }
                    />
                  )}
                  {draftIsRefund && (
                    <Alert severity="warning">
                      A refund reduces the term&apos;s paid amount. It is sent for verification like
                      a payment. Refundable now:{" "}
                      <strong>{formatAudCompact(refundableAvailable)}</strong>
                      {pendingRefundTotal > 0 ? (
                        <>
                          {" "}
                          (verified paid {formatAudCompact(verifiedPaidClamped)}, pending refunds{" "}
                          {formatAudCompact(pendingRefundTotal)})
                        </>
                      ) : verifiedPaidClamped > 0 ? (
                        <> (verified paid {formatAudCompact(verifiedPaidClamped)})</>
                      ) : null}
                      . Verifying refunds cannot exceed this total.
                    </Alert>
                  )}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                    <TextField
                      type="number"
                      label={draftIsRefund ? "Refund amount (AUD)" : "Amount (AUD)"}
                      size="small"
                      required
                      value={draft.amount}
                      onChange={(e) => setDraft((d) => ({ ...d, amount: e.target.value }))}
                      inputProps={{ min: 0, step: "0.01" }}
                      sx={{ flex: 1, minWidth: 140 }}
                    />
                    <TextField
                      type="date"
                      label="Payment date"
                      size="small"
                      required
                      value={draft.date}
                      onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ max: "9999-12-31" }}
                      sx={{ flex: 1, minWidth: 160 }}
                    />
                    <TextField
                      label="Reference"
                      size="small"
                      value={draft.reference}
                      onChange={(e) => setDraft((d) => ({ ...d, reference: e.target.value }))}
                      placeholder="Receipt #, transaction ID"
                      sx={{ flex: 1, minWidth: 180 }}
                    />
                  </Box>

                  <TextField
                    label="Notes"
                    size="small"
                    multiline
                    minRows={3}
                    maxRows={6}
                    fullWidth
                    value={draft.notes}
                    onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                    placeholder="Internal notes for accounts or admissions (e.g. partial payment, bank transfer details)"
                  />

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 0.5, display: "block" }}
                    >
                      Attachments
                    </Typography>
                    <UploadFile
                      data={uploadData}
                      setData={setUploadData}
                      imageKey="attachments"
                      type="multiple"
                      label="receipt or proof of payment"
                    />
                  </Box>

                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                      variant="contained"
                      startIcon={editingPaymentId ? null : <AddIcon />}
                      onClick={editingPaymentId ? handleResubmitClick : handleAddClick}
                      disabled={
                        isSyncing || coeBlocksSubmit || (draftIsRefund && refundEntryDisabled)
                      }
                    >
                      {isSyncing ? "Saving…" : primaryLabel}
                    </Button>
                    {editingPaymentId && (
                      <Button variant="outlined" onClick={resetDraft} disabled={isSyncing}>
                        Cancel edit
                      </Button>
                    )}
                  </Box>
                </Stack>
              </Paper>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={isSyncing}>
            {isSyncing ? "Saving…" : termId && hasPendingChanges ? "Save & Close" : "Close"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={() => !isSyncing && setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {willAutoVerify && !editingPaymentId
            ? `Record & verify ${draftIsRefund ? "refund" : "payment"}?`
            : "Send for verification?"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" component="div">
            {willAutoVerify && !editingPaymentId ? (
              <>
                This {draftIsRefund ? "refund" : "payment"} will be recorded and{" "}
                <strong>verified immediately</strong> without routing to Accounts.
              </>
            ) : (
              <>
                This {draftIsRefund ? "refund" : "payment"} will be sent to accounts for
                verification. Verifiers will review the amount, date, notes, and attachments, and
                can also see any <strong>term notes</strong> added on this term in the schedule.
              </>
            )}
            {draftIsRefund && (
              <>
                {" "}
                Once verified, the term&apos;s paid amount drops accordingly and a fully-paid term
                may reopen.
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={isSyncing}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirm} disabled={isSyncing}>
            {isSyncing
              ? "Saving…"
              : willAutoVerify && !editingPaymentId
              ? "Record & verify"
              : "Send for verification"}
          </Button>
        </DialogActions>
      </Dialog>

      <PendingPaymentDialog
        open={Boolean(pendingChange.payment)}
        mode={pendingChange.mode}
        payment={pendingChange.payment}
        reasonOnly={isCreditAdjustment(pendingChange.payment)}
        saveEdit={(payment, values) =>
          editPendingTermPayment(payment.id, {
            expectedRevision: payment.revision,
            ...termPaymentBody(payment, values),
          })
        }
        withdraw={(payment, reason) =>
          withdrawTermPayment(payment.id, { expectedRevision: payment.revision, reason })
        }
        onClose={() => setPendingChange({ mode: null, payment: null })}
        onChanged={refreshAfterPendingChange}
        onDone={(mode, values) => {
          const uuid = pendingChange.payment?.uuid;
          if (mode === "withdraw") {
            onChangePayments?.(payments.filter((p) => p.id !== uuid));
          } else if (!isCreditAdjustment(pendingChange.payment)) {
            applyLocalValues(uuid, values);
          }
        }}
        onResubmit={
          termId
            ? async (payment, values) => {
                const body = termPaymentBody(payment, values);
                await resubmitTermPayment(termId, payment.uuid, body);
                if (!isCreditAdjustment(payment)) applyLocalValues(payment.uuid, body);
              }
            : undefined
        }
        onRecordNew={(values) => {
          setEditingPaymentId(null);
          setDraft({
            ...createEmptyPaymentDraft(),
            entryType: isRefundEntry(pendingChange.payment?.entryType) ? "REFUND" : "PAYMENT",
            amount: values.amount ?? "",
            date: values.date ?? "",
            reference: values.reference ?? "",
            notes: values.notes ?? "",
          });
          setUploadData({ attachments: values.attachments || [] });
        }}
      />

      <Dialog
        open={deleteDialog.open}
        onClose={() => !isSyncing && setDeleteDialog({ open: false, payment: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Permanently delete this{" "}
          {isCreditAdjustment(deleteDialog.payment)
            ? "credit move"
            : isRefundEntry(deleteDialog.payment?.entryType)
            ? "refund"
            : "payment"}
          ?
        </DialogTitle>
        <DialogContent>
          {deleteDialogBlockReason ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteDialogBlockReason}
            </Alert>
          ) : null}
          <Typography variant="body2" color="text.secondary" component="div">
            {isCreditAdjustment(deleteDialog.payment) ? (
              <>
                Super admin action. This is one half of a credit move, so{" "}
                <strong>both halves are deleted</strong> (
                {creditAdjustmentLabel(deleteDialog.payment).toLowerCase()}) and both terms&apos;
                paid amounts go back to what they were before it. Either term may reopen.
              </>
            ) : (
              <>
                Super admin action. If the term is fully verified this will reopen it. Re-add the
                entry to the correct term afterwards. The deleted entry&apos;s notes and receipts
                are kept in the term history.
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, payment: null })}
            disabled={isSyncing}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmSuperAdminDelete}
            disabled={isSyncing || Boolean(deleteDialogBlockReason)}
          >
            {isSyncing ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={removeDialog.open}
        onClose={() => setRemoveDialog({ open: false, paymentId: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove this entry from the term?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This removes an unsynced schedule line — it is not a verified payment and does not
            affect the verified paid total. The change is saved when you save the term schedule.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialog({ open: false, paymentId: null })}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmRemove}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(resubmitMove.payment)}
        onClose={() => !isSyncing && setResubmitMove({ payment: null, reason: "" })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Resubmit this credit move?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Both halves of the move go back to accounts for verification. The amount and terms stay
            the same; to change them, leave this one rejected and start a new move.
          </Typography>
          {resubmitMove.payment?.rejectionFeedback && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Rejection reason: {resubmitMove.payment.rejectionFeedback}
            </Alert>
          )}
          <TextField
            label="Reason"
            required
            multiline
            minRows={2}
            fullWidth
            size="small"
            value={resubmitMove.reason}
            onChange={(e) => setResubmitMove((m) => ({ ...m, reason: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setResubmitMove({ payment: null, reason: "" })}
            disabled={isSyncing}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmResubmitMove}
            disabled={isSyncing || !resubmitMove.reason.trim()}
          >
            {isSyncing ? "Saving…" : "Resubmit for verification"}
          </Button>
        </DialogActions>
      </Dialog>

      {creditDialogOpen && (
        <CreditAdjustmentDialog
          open={creditDialogOpen}
          onClose={() => setCreditDialogOpen(false)}
          termId={termId}
          termLabel={termLabel}
          onDone={onPaymentsSynced}
        />
      )}
    </>
  );
}
